import { apiService, type Location, type Modelo, type Diligence } from './api';
import { logger } from '../utils/logger';

/**
 * Serviço de banco de dados que utiliza a API do Next.js hospedada no Vercel
 * Este serviço atua como uma camada de abstração entre os componentes e a API
 */
export class DatabaseService {
  private isConnected: boolean = false;

  constructor() {
    this.testConnection();
  }

  /**
   * Testa a conexão com a API
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await apiService.testConnection();
      this.isConnected = response.success;

      if (this.isConnected) {
        logger.log('✅ Conexão com a API estabelecida com sucesso');
      } else {
        logger.error('❌ Falha na conexão com a API:', response.error);
      }

      return this.isConnected;
    } catch (error) {
      logger.error('❌ Erro ao testar conexão com a API:', error);
      this.isConnected = false;
      return false;
    }
  }

  /**
   * Verifica se está conectado à API
   */
  isConnectedToApi(): boolean {
    return this.isConnected;
  }

  /**
   * MÉTODOS PARA LOCALIZAÇÕES
   */

  /**
   * Busca todas as localizações ou filtra por notificador
   * Nova funcionalidade: permite filtrar por id_notificador
   */
  async getLocations(id_notificador?: number): Promise<Location[]> {
    try {
      const response = await apiService.getLocations(id_notificador);

      if (response.success && response.data) {
        return response.data;
      } else {
        logger.error('Erro ao buscar localizações:', response.error);
        return [];
      }
    } catch (error) {
      logger.error('Erro ao buscar localizações:', error);
      return [];
    }
  }

  /**
   * Busca uma localização por ID
   */
  async getLocationById(id: number): Promise<Location | null> {
    try {
      const response = await apiService.getLocationById(id);

      if (response.success && response.data) {
        return response.data;
      } else {
        logger.error('Erro ao buscar localização:', response.error);
        return null;
      }
    } catch (error) {
      logger.error('Erro ao buscar localização:', error);
      return null;
    }
  }

  /**
   * Busca uma localização por ID com detalhes completos
   */
  async getLocationByIdWithDetails(id: number): Promise<Location | null> {
    try {
      const response = await apiService.getLocationByIdWithFilters(id, true);

      if (response.success && response.data) {
        return response.data;
      } else {
        logger.error('Erro ao buscar localização com detalhes:', response.error);
        return null;
      }
    } catch (error) {
      logger.error('Erro ao buscar localização com detalhes:', error);
      return null;
    }
  }

  /**
   * Cria uma nova localização
   */
  async createLocation(location: Omit<Location, 'id_location'>): Promise<Location | null> {
    try {
      const response = await apiService.createLocation(location);

      if (response.success && response.data) {
        logger.log('✅ Localização criada com sucesso:', response.data);
        return response.data;
      } else {
        logger.error('Erro ao criar localização:', response.error);
        return null;
      }
    } catch (error) {
      logger.error('Erro ao criar localização:', error);
      return null;
    }
  }

  /**
   * Atualiza uma localização
   */
  async updateLocation(id: number, updates: Partial<Location>): Promise<Location | null> {
    try {
      const response = await apiService.updateLocation(id, updates);

      if (response.success && response.data) {
        logger.log('✅ Localização atualizada com sucesso:', response.data);
        return response.data;
      } else {
        logger.error('Erro ao atualizar localização:', response.error);
        return null;
      }
    } catch (error) {
      logger.error('Erro ao atualizar localização:', error);
      return null;
    }
  }

  /**
   * Remove uma localização
   */
  async deleteLocation(id: number): Promise<boolean> {
    try {
      const response = await apiService.deleteLocation(id);

      if (response.success) {
        logger.log('✅ Localização removida com sucesso');
        return true;
      } else {
        logger.error('Erro ao remover localização:', response.error);
        return false;
      }
    } catch (error) {
      logger.error('Erro ao remover localização:', error);
      return false;
    }
  }

  /**
   * MÉTODOS PARA DILIGÊNCIAS
   */

  /**
   * Busca todas as diligências (dados completos)
   * ✅ CORRIGIDO! Endpoint funcionando perfeitamente
   */
  async getDiligencias(): Promise<any[]> {
    try {
      const response = await apiService.getDiligencias();

      if (response.success && response.data) {
        return response.data;
      } else {
        logger.error('Erro ao buscar diligências:', response.error);
        return [];
      }
    } catch (error) {
      logger.error('Erro ao buscar diligências:', error);
      return [];
    }
  }

  /**
   * Cria uma nova diligência
   */
  async createDiligencia(diligencia: Omit<Diligence, 'id_deligencia'>): Promise<any> {
    try {
      const response = await apiService.createDiligencia(diligencia);

      if (response.success && response.data) {
        logger.log('✅ Diligência criada com sucesso:', response.data);
        return response;
      } else {
        logger.error('Erro ao criar diligência:', response.error);
        return response;
      }
    } catch (error) {
      logger.error('Erro ao criar diligência:', error);
      return { success: false, error: 'Erro interno ao criar diligência' };
    }
  }

  /**
   * MÉTODOS PARA MODELOS
   */

  /**
   * Busca todos os modelos
   */
  async getModelos(id_status?: number): Promise<Modelo[]> {
    try {
      const response = await apiService.getModelos(id_status);

      if (response.success && response.data) {
        return response.data;
      } else {
        logger.error('Erro ao buscar modelos:', response.error);
        return [];
      }
    } catch (error) {
      logger.error('Erro ao buscar modelos:', error);
      return [];
    }
  }

  /**
   * Busca um modelo por ID
   */
  async getModeloById(id: number): Promise<Modelo | null> {
    try {
      const response = await apiService.getModeloById(id);

      if (response.success && response.data) {
        return response.data;
      } else {
        logger.error('Erro ao buscar modelo:', response.error);
        return null;
      }
    } catch (error) {
      logger.error('Erro ao buscar modelo:', error);
      return null;
    }
  }

}

// Instância singleton do serviço de banco de dados
export const databaseService = new DatabaseService();

// Exportações para facilitar o uso
export default databaseService;
